import { useEffect, useState } from "react";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, ExternalLink, OctagonX, Play, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

interface Point {
  id: string; type: string; titre: string; jours_attente: number; criticite: number; urgence: number; priorite: number;
  niveau: "maintenant" | "cette_semaine" | "peut_attendre"; action: string; bloque?: string[]; preuves?: string[]; lien?: string | null; cout_attente_eur_mois?: number;
}
interface Etat {
  a_confirmer: Point[];
  comportement: { oubli: number | null; defiance: number | null; reponses_donnees: number; taux_validation_plans: number | null; plans_presentes: number; lecture: string };
  blocage: { points_en_attente: number; plus_ancien_jours: number; cout_attente_eur_par_mois: number };
}

const NIVEAU: Record<string, { libelle: string; classe: string }> = {
  maintenant: { libelle: "À faire maintenant", classe: "bg-red-500/15 text-red-400 border-red-500/30" },
  cette_semaine: { libelle: "Cette semaine", classe: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  peut_attendre: { libelle: "Peut attendre", classe: "bg-muted text-muted-foreground border-border" },
};
const MOTIFS: Record<string, string> = { pas_le_temps: "Pas le temps", je_ne_comprends_pas: "Je ne comprends pas", pas_confiance: "Je ne fais pas confiance", trop_risque: "Trop risqué", pas_d_accord: "Pas d'accord sur le fond" };
const eur = (n: number) => `${Math.round(n).toLocaleString("fr-FR")} €`;

interface ContenuAg { id: string; variante: string; angle: string; statut: string; planifie_le?: string; publie_le?: string; score_qualite?: number; resultats?: { clics?: number }; erreur?: string }
interface CampagneAg { campagne: { id: string; nom: string; mandat: { actif: boolean; canaux: string[]; max_par_semaine: number; jusqu_au: string; budget_eur?: number } }; contenus: ContenuAg[]; score: { etat: string; action: string; resultat_pct: number | null; seuil_correction: number } }
const STATUT_CONTENU: Record<string, string> = { valide: "planifié", publie: "publié", rejete: "retiré", echec: "échec", a_verifier: "à vérifier", publication_en_cours: "publication en cours", brouillon: "brouillon" };
const dateFr = (iso?: string) => (iso ? new Date(iso).toLocaleString("fr-FR", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" }) : "—");

function AgentsCampagne() {
  const [camps, setCamps] = useState<CampagneAg[]>([]);
  const charger = async () => { const r = await fetch(`${BACKEND}/api/agents-campagne`, { credentials: "include" }); if (r.ok) setCamps((await r.json()).campagnes || []); };
  useEffect(() => { charger(); const t = setInterval(charger, 60000); return () => clearInterval(t); }, []);
  const agir = async (id: string, action: "stop" | "reprendre") => {
    if (action === "stop" && !window.confirm("Arrêter immédiatement les publications de cette campagne ? Les contenus restent en l'état.")) return;
    const r = await fetch(`${BACKEND}/api/agents-campagne/${id}/${action}`, { method: "POST", credentials: "include" });
    if (r.ok) { toast.success(action === "stop" ? "Mandat arrêté : plus rien ne sera publié" : "Mandat repris"); charger(); } else toast.error("Action impossible");
  };
  if (camps.length === 0) return null;
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold pt-4">Agents de campagne</h2>
      {camps.map((c) => (
        <Card key={c.campagne.id}>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">{c.campagne.nom}</CardTitle>
              <Badge variant="outline" className={c.campagne.mandat.actif ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-red-500/15 text-red-400 border-red-500/30"}>{c.campagne.mandat.actif ? "Mandat actif" : "Mandat arrêté"}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="text-muted-foreground">{c.campagne.mandat.canaux.join(", ")} · {c.campagne.mandat.max_par_semaine} publications par semaine au plus · budget {c.campagne.mandat.budget_eur ?? 0} € · jusqu'au {c.campagne.mandat.jusqu_au}</div>
            <div>État : <b>{c.score.etat}</b>{c.score.resultat_pct != null ? ` · résultat à ${c.score.resultat_pct} % de la cible (correction sous ${c.score.seuil_correction} %)` : " · aucune mesure comparable pour l'instant"}. <span className="text-muted-foreground">{c.score.action}</span></div>
            <ul className="space-y-1">
              {c.contenus.filter((k) => k.planifie_le || k.statut === "publie").map((k) => (
                <li key={k.id} className="flex flex-wrap justify-between gap-2 rounded-md bg-muted/40 px-3 py-1.5">
                  <span>Post {k.variante} · {k.angle}</span>
                  <span className="text-muted-foreground">{STATUT_CONTENU[k.statut] || k.statut} · {dateFr(k.publie_le || k.planifie_le)}{k.resultats?.clics != null ? ` · ${k.resultats.clics} clic(s)` : ""}</span>
                </li>
              ))}
            </ul>
            {c.campagne.mandat.actif
              ? <Button size="sm" variant="destructive" className="gap-1.5" onClick={() => agir(c.campagne.id, "stop")}><OctagonX className="h-4 w-4" />Arrêter le mandat</Button>
              : <Button size="sm" className="gap-1.5" onClick={() => agir(c.campagne.id, "reprendre")}><Play className="h-4 w-4" />Reprendre</Button>}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export default function Validations() {
  const [etat, setEtat] = useState<Etat | null>(null);
  const [refus, setRefus] = useState<string | null>(null);
  const [motif, setMotif] = useState("pas_le_temps");
  const [occupe, setOccupe] = useState<string | null>(null);

  const charger = async () => {
    const r = await fetch(`${BACKEND}/api/validations`, { credentials: "include" });
    if (r.ok) setEtat(await r.json());
  };
  useEffect(() => { charger(); }, []);

  const appeler = async (chemin: string, corps: object, message: string, id: string) => {
    setOccupe(id);
    try {
      const r = await fetch(`${BACKEND}/api/validations/${chemin}`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(corps) });
      const d = await r.json().catch(() => ({}));
      if (r.ok) { toast.success(message); setRefus(null); await charger(); }
      else toast.error(typeof d.detail === "string" ? d.detail : "Action impossible");
    } finally { setOccupe(null); }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 p-5 md:p-10 overflow-y-auto">
          <div className="max-w-3xl mx-auto space-y-6">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Validations</h1>
              <p className="text-muted-foreground mt-1">Ce que Grafton attend de vous, du plus urgent au moins urgent. Tant qu'un point n'est pas validé, Grafton prépare mais ne publie, n'envoie et ne dépense rien.</p>
            </div>

            {etat && etat.a_confirmer.length > 0 && (
              <Card><CardContent className="pt-5 flex items-start gap-3">
                <ShieldAlert className="h-6 w-6 text-amber-400 shrink-0 mt-0.5" />
                <div className="text-sm space-y-1">
                  <div><b>{etat.blocage.points_en_attente}</b> point(s) en attente, le plus ancien depuis <b>{etat.blocage.plus_ancien_jours.toFixed(0)}</b> jour(s).</div>
                  {etat.blocage.cout_attente_eur_par_mois > 0 && <div className="text-muted-foreground">Coût de l'attente : environ {eur(etat.blocage.cout_attente_eur_par_mois)} par mois d'espérance de chiffre d'affaires <i>(hypothèse du générateur d'actions, pas une mesure)</i>.</div>}
                  <div className="text-muted-foreground">{etat.comportement.lecture}.{etat.comportement.taux_validation_plans != null && ` Plans de briefing validés : ${etat.comportement.taux_validation_plans} % sur ${etat.comportement.plans_presentes}.`}</div>
                </div>
              </CardContent></Card>
            )}

            {etat && etat.a_confirmer.length === 0 && (
              <Card><CardContent className="pt-5 flex items-center gap-3"><CheckCircle2 className="h-6 w-6 text-emerald-400" /><span>Rien à confirmer : tout est à jour.</span></CardContent></Card>
            )}

            {etat?.a_confirmer.map((p) => (
              <Card key={p.id}>
                <CardHeader className="pb-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className={NIVEAU[p.niveau].classe} variant="outline">{NIVEAU[p.niveau].libelle}</Badge>
                    <span className="text-xs text-muted-foreground">urgence {p.urgence}/100 · criticité {p.criticite}/100</span>
                  </div>
                  <CardTitle className="text-base leading-snug pt-1">{p.titre}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex items-center gap-1.5 text-muted-foreground"><Clock className="h-3.5 w-3.5" />En attente depuis {p.jours_attente.toFixed(0)} jour(s)
                    {p.cout_attente_eur_mois ? ` · environ ${eur(p.cout_attente_eur_mois)} par mois d'espérance (hypothèse)` : ""}</div>
                  {p.bloque && p.bloque.length > 0 && <div><b>Bloque :</b> {p.bloque.join(", ")}.</div>}
                  <div><b>Ce que vous validez :</b> {p.action}</div>
                  {p.preuves && p.preuves.length > 0 && <ul className="list-disc pl-5 text-muted-foreground">{p.preuves.map((x, i) => <li key={i}>{x}</li>)}</ul>}

                  {refus === p.id ? (
                    <div className="flex flex-wrap gap-2 items-center pt-1">
                      <select className="border rounded-md bg-background px-2 h-9" value={motif} onChange={(e) => setMotif(e.target.value)}>
                        {Object.entries(MOTIFS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                      <Button size="sm" variant="destructive" disabled={occupe === p.id} onClick={() => appeler("reponse", { item_id: p.id, reponse: "refuse", motif }, "Refus enregistré, merci du motif", p.id)}>Confirmer le refus</Button>
                      <Button size="sm" variant="ghost" onClick={() => setRefus(null)}>Annuler</Button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {p.type === "plan_orchestration" && p.lien
                        ? <Button size="sm" onClick={() => window.open(p.lien!, "_blank")} className="gap-1.5"><ExternalLink className="h-3.5 w-3.5" />Voir le plan et le valider</Button>
                        : <Button size="sm" disabled={occupe === p.id} onClick={() => appeler("valider", { item_id: p.id }, "Validé", p.id)} className="gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" />Valider</Button>}
                      <Button size="sm" variant="outline" disabled={occupe === p.id} onClick={() => appeler("reponse", { item_id: p.id, reponse: "plus_tard" }, "Noté : plus tard", p.id)}>Plus tard</Button>
                      <Button size="sm" variant="outline" onClick={() => setRefus(p.id)}>Refuser</Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            <AgentsCampagne />
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
